<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Hero Section -->
    <section class="relative overflow-hidden" style="background: linear-gradient(to bottom right, #A3E4DB, #C4E49F);">
      <div class="absolute inset-0 bg-white/30 backdrop-blur-sm"></div>
      <div class="relative container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl py-12 sm:py-16 lg:py-20">
        <div class="grid lg:grid-cols-2 gap-12 items-center">
          <!-- Left Content -->
          <div class="text-gray-800 lg:pr-12">
            <h1 class="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              <span style="color: #F4D35E;">即刻互動</span>聯絡簿
            </h1>
            <p class="text-base sm:text-lg lg:text-xl mb-8 text-gray-700 leading-relaxed max-w-xl">
              提供家長與教師即時溝通的平台，快速查看孩子的學習與健康狀況，提升回應效率，讓雙方更輕鬆互動。
            </p>
          </div>

          <!-- Right Image -->
          <div class="flex justify-center lg:justify-end">
            <div class="relative w-full max-w-xl">
              <img src="@/assets/img/pic3.png" alt="即刻互動聯絡簿"
                class="rounded-3xl shadow-2xl object-cover w-full h-auto" />
              <!-- Decorative Circles -->
              <div class="absolute -top-4 -right-4 w-20 h-20 bg-yellow-300 rounded-full opacity-20"></div>
              <div class="absolute -bottom-4 -left-4 w-16 h-16 bg-teal-300 rounded-full opacity-20"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Decorative Elements -->
      <div class="absolute top-0 right-0 w-1/3 h-full opacity-10">
        <div class="w-full h-full bg-gradient-to-l from-white to-transparent"></div>
      </div>
    </section>

    <section class="py-12 bg-white">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <h2 class="text-3xl sm:text-4xl font-bold text-gray-800 mb-12 text-center">
          核心功能介紹
        </h2>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <div class="bg-gray-50 p-6 rounded-xl shadow hover:shadow-md transition">
            <h3 class="text-xl font-semibold text-sky-600 mb-2">餵藥提醒</h3>
            <p class="text-gray-600">
              透過自動提醒家長與教師餵藥時間，確保孩子按時服藥，守護健康安全。
            </p>
          </div>
          <div class="bg-gray-50 p-6 rounded-xl shadow hover:shadow-md transition">
            <h3 class="text-xl font-semibold text-emerald-600 mb-2">體溫檢查</h3>
            <p class="text-gray-600">
              透過即時體溫檢查，確保學生健康狀況，及早發現潛在問題。
            </p>
          </div>
          <div class="bg-gray-50 p-6 rounded-xl shadow hover:shadow-md transition">
            <h3 class="text-xl font-semibold text-orange-600 mb-2">班級叮嚀</h3>
            <p class="text-gray-600">
              提供班級重要通知與提醒，確保家長與教師之間的即時溝通。
            </p>
          </div>
          <div class="bg-gray-50 p-6 rounded-xl shadow hover:shadow-md transition">
            <h3 class="text-xl font-semibold text-sky-600 mb-2">聯絡簿簽名</h3>
            <p class="text-gray-600">
              家長可線上簽名確認聯絡簿內容，提升回覆效率與透明度。
            </p>
          </div>
          <div class="bg-gray-50 p-6 rounded-xl shadow hover:shadow-md transition">
            <h3 class="text-xl font-semibold text-indigo-600 mb-2">留言板</h3>
            <p class="text-gray-600">
              家長和教師可透過留言板即時留言交流，促進雙方溝通與互動。
            </p>
          </div>
          <div class="bg-gray-50 p-6 rounded-xl shadow hover:shadow-md transition">
            <h3 class="text-xl font-semibold text-orange-600 mb-2">當日通知單</h3>
            <p class="text-gray-600">
              即時推送每日重要通知與活動資訊，讓家長掌握孩子校園最新狀況。
            </p>
          </div>
          <div class="bg-gray-50 p-6 rounded-xl shadow hover:shadow-md transition">
            <h3 class="text-xl font-semibold text-emerald-600 mb-2">學習狀況</h3>
            <p class="text-gray-600">
              詳細記錄學生學習進度與成果，便於家長隨時了解孩子的學習狀況。
            </p>
          </div>
          <div class="bg-gray-50 p-6 rounded-xl shadow hover:shadow-md transition">
            <h3 class="text-xl font-semibold text-indigo-600 mb-2">簽到記錄</h3>
            <p class="text-gray-600">
              精準管理學生每日簽到狀況，提升校園安全管理效率。
            </p>
          </div>
        </div>
      </div>
    </section>



    <!-- 介面導覽區 -->
    <section class="py-12 bg-gray-50">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <h2 class="text-3xl sm:text-4xl font-bold text-gray-800 mb-12 text-center">
          介面導覽
        </h2>
        <p class="text-lg text-gray-600 mb-8 text-center">
          點擊下方圖片可放大檢視
        </p>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <div v-for="(item, index) in featureList" :key="index" class="text-center space-y-4">
            <div>
              <h3 :class="'text-2xl font-semibold mb-2 ' + item.color">{{ item.title }}</h3>
              <p class="text-gray-700 text-base px-2">{{ item.description }}</p>
            </div>
            <div class="cursor-pointer inline-block" @click="showLightbox(index)">
              <img :src="item.img" :alt="item.title"
                class="rounded-xl shadow-md w-64 h-40 mx-auto object-cover transition hover:scale-105 duration-200" />
            </div>
          </div>
        </div>

        <!-- 圖片燈箱 -->
        <vue-easy-lightbox :visible="lightboxVisible" :imgs="featureList.map(item => item.img)" :index="lightboxIndex"
          @hide="lightboxVisible = false" />
      </div>
    </section>

    <!-- CTA Section -->
    <section class="py-12 -mb-3 bg-white">
      <div class="container mx-auto px-6 text-center">
        <div class="max-w-4xl mx-auto">
          <h2 class="text-4xl font-bold text-gray-800 mb-6">
            立即體驗即刻互動聯絡簿
          </h2>
          <p class="text-xl text-gray-700 mb-8 max-w-2xl mx-auto">
            專業團隊提供完整的產品諮詢與技術支援，協助您快速部署
          </p>
          <div class="flex flex-col sm:flex-row gap-4 justify-center">
            <router-link to="/contactus">
              <button aria-label="聯絡我們瞭解更多"
                  class="text-black px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-bold text-base sm:text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                  style="background-color: #F4D35E; --tw-bg-opacity: 1;"
                  onmouseover="this.style.backgroundColor='#fce788'"
                  onmouseout="this.style.backgroundColor='#F4D35E'"
                >
                聯絡我們瞭解更多
              </button>
            </router-link>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import VueEasyLightbox from 'vue-easy-lightbox'
import img1 from '@/assets/img/pic4.png'
import img2 from '@/assets/img/pic5.png'
import img3 from '@/assets/img/pic6.png'
import img4 from '@/assets/img/pic7.png'
import img5 from '@/assets/img/pic8.png'
import img6 from '@/assets/img/pic9.png'
import img7 from '@/assets/img/pic10.png'
import img8 from '@/assets/img/pic11.png'


const lightboxVisible = ref(false)
const lightboxIndex = ref(0)

const showLightbox = (index) => {
  lightboxIndex.value = index
  lightboxVisible.value = true
}

const featureList = [
  {
    title: '餵藥提醒',
    img: img1,
    color: 'text-sky-600', // 藍色
  },
  {
    title: '體溫檢查',
    img: img2,
    color: 'text-emerald-600', // 綠色
  },
  {
    title: '班級叮嚀',
    img: img3,
    color: 'text-orange-600', // 橙色
  },
  {
    title: '聯絡簿簽名',
    img: img4,
    color: 'text-sky-600', // 藍色
  },
  {
    title: '留言板',
    img: img5,
    color: 'text-indigo-600', // 保持藍色
  },
  {
    title: '當日通知單',
    img: img6,
    color: 'text-orange-600', // 橙色
  },
  {
    title: '學習狀況',
    img: img7,
    color: 'text-emerald-600', // 綠色
  },
  {
    title: '簽到記錄',
    img: img8,
    color: 'text-indigo-600', // 藍色
  }
]

</script>

<style lang="scss" scoped>
// 修正 z-index 層級問題
.text-gray-800 {
  position: relative;
  z-index: 1;
}

// Hero section 的背景層級
.absolute.inset-0 {
  z-index: 0;
}

// 裝飾元素層級
.absolute.top-0.right-0 {
  z-index: 0;
}

section.bg-white {
  position: relative;
  z-index: 1;
}

// 卡片 hover 效果
.group:hover .w-6.h-6 {
  transform: scale(1.1);
}

// 響應式設計
@media (max-width: 768px) {
  .grid.md\\:grid-cols-2 {
    grid-template-columns: 1fr;
  }

  .grid.md\\:grid-cols-3 {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  .text-3xl.sm\\:text-4xl.lg\\:text-6xl {
    font-size: 2rem;
  }

  .text-base.sm\\:text-lg.lg\\:text-xl {
    font-size: 1rem;
  }
}

// 動畫效果
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fadeInUp {
  animation: fadeInUp 0.6s ease-out;
}

// 按鈕 hover 效果
button {
  transition: all 0.3s ease;
}

button:hover {
  transform: translateY(-2px);
}

// 響應式按鈕
@media (max-width: 640px) {
  .flex.flex-col.sm\\:flex-row {
    flex-direction: column;
  }

  button {
    width: 100%;
    margin-bottom: 0.5rem;
  }
}

// 圖片容器樣式
img {
  transition: transform 0.3s ease;
}

img:hover {
  transform: scale(1.02);
}

// 功能卡片 hover 效果
.flex.items-start.gap-4 {
  transition: all 0.3s ease;
  padding: 1rem;
  border-radius: 1rem;
}

.flex.items-start.gap-4:hover {
  background-color: rgba(59, 130, 246, 0.05);
  transform: translateY(-2px);
}

// 漸層背景
.bg-gradient-to-br.from-sky-50.to-emerald-50 {
  background: linear-gradient(135deg, #f0f9ff 0%, #ecfdf5 100%);
}
</style>